import { useRef, useState } from 'react';
import { Upload, X, Loader2, Image as ImageIcon, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { UploadFolderGrid } from './UploadFolderGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

async function uploadFile(file) {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BACKEND_URL}/api/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || 'Yükleme başarısız');
  }
  const data = await res.json();
  return data.url.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`;
}

export function ImagePicker({ value, onChange, label = 'Görsel', testId, height = 160 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);

  const pickFromFolder = (url) => {
    onChange(url);
    setFolderOpen(false);
  };

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow reselecting same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin');
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error('Dosya çok büyük (max 10 MB)');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadFile(file);
      onChange(url);
      toast.success('Görsel yüklendi');
    } catch (err) {
      toast.error(err.message || 'Yükleme hatası');
    } finally {
      setUploading(false);
    }
  };

  const clear = () => onChange('');

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleSelect}
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {value ? (
        <div className="relative group rounded-xl overflow-hidden border-2 border-amber-200 bg-amber-50" style={{ height }}>
          <img src={value} alt={label} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-white text-amber-900 hover:bg-amber-50 shadow"
              data-testid={testId ? `${testId}-change` : undefined}
            >
              <Upload className="w-3.5 h-3.5 inline mr-1" />Dosyadan
            </button>
            <button
              type="button"
              onClick={() => setFolderOpen(true)}
              disabled={uploading}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-white text-amber-900 hover:bg-amber-50 shadow"
              data-testid={testId ? `${testId}-folder` : undefined}
            >
              <FolderOpen className="w-3.5 h-3.5 inline mr-1" />Klasörden
            </button>
            <button
              type="button"
              onClick={clear}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 shadow"
              data-testid={testId ? `${testId}-remove` : undefined}
            >
              <X className="w-3.5 h-3.5 inline mr-1" />Kaldır
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-amber-300 rounded-xl bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 transition-colors text-amber-700 disabled:opacity-60"
            style={{ height }}
            data-testid={testId ? `${testId}-upload` : undefined}
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-xs">Yükleniyor...</span>
              </>
            ) : (
              <>
                <ImageIcon className="w-7 h-7 text-amber-500" />
                <div className="text-center">
                  <p className="text-sm font-medium">Dosyadan Seç</p>
                  <p className="text-[11px] text-amber-600/80">JPG, PNG, WEBP · max 10 MB</p>
                </div>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setFolderOpen(true)}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 border border-amber-300 rounded-xl bg-white hover:bg-amber-50 transition-colors text-amber-800 text-sm font-medium py-2.5 disabled:opacity-60"
            data-testid={testId ? `${testId}-folder` : undefined}
          >
            <FolderOpen className="w-4 h-4" /> Upload Klasöründen Seç
          </button>
        </div>
      )}

      <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Klasöründen Seç</DialogTitle>
          </DialogHeader>
          <UploadFolderGrid onSelect={pickFromFolder} selectedUrl={value} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
